/**
 * The licence certificate and the states it can be in.
 *
 * A BroadPaper licence is a signed certificate, not a secret. It says who
 * bought what and until when, BroadPaper signs it, and the SDK checks that
 * signature with a public key compiled into the package. Nothing here talks to
 * a network, reads a machine identifier or writes a file: a licence that is
 * valid on a laptop with no route to the internet is valid, full stop.
 */

/**
 * Which licence was bought. Not a feature gate — both plans unlock exactly the
 * same software. The difference is who the resulting application may be put in
 * front of, which is a contractual matter and is presented as one in the
 * account area and the licence agreement.
 *
 * These are the `id`s in `shared/plans.json`, deliberately: that file is the
 * one machine-readable statement of the commercial model, and a certificate
 * that quotes a different vocabulary from the thing that priced it is a
 * certificate somebody eventually has to write a mapping table for.
 */
export type Plan = "internal" | "saas" | "evaluation";

/**
 * The free, time-limited licence a developer gets from their account in
 * exchange for telling us who they are.
 *
 * It removes the mark while somebody evaluates, and nothing else: it permits no
 * production use, and it has no grace period — the day it ends, the mark comes
 * back. It is not in `shared/plans.json`, because nothing sells it.
 *
 * SDKs before 1.0 do not know the word and report such a licence as
 * `unsupported`, which keeps the mark and says to upgrade. That is the fail-safe
 * direction, and `tests/site/licence-format.test.ts` holds 0.4.1 to it.
 */
export const EVALUATION_PLAN = "evaluation" satisfies Plan;

/**
 * How the licence is held.
 *
 * `subscription` is a term: the right to use the software ends with the term,
 * and so does the right to updates. It is the only thing sold today.
 *
 * `perpetual` is reserved. It would mean the right to use never ends, while
 * the right to updates still does — `validUntil` null, `updatesUntil` set. The
 * field exists now so that offering one later is a change to what the issuer
 * writes rather than a change to the certificate format, which would mean
 * reissuing every licence in the field. **This SDK does not honour one**: see
 * `readPayload` in `format.ts` for why refusing is the only safe answer.
 */
export type LicenseType = "subscription" | "perpetual";

/** The product a licence is for. Present so one signing key can serve more later. */
export const PRODUCT = "broadpaper-sdk";

/**
 * The signed payload.
 *
 * `organisationId` is the durable identifier of the buying organisation — a
 * Clerk organisation id — rather than an email address, because people change
 * addresses and organisations outlive their founders. It is *not* the Creem
 * customer id, which identifies the same buyer to the payment processor and is
 * a different string; the API keeps both, and naming this one for the
 * organisation is what stops the two being confused at a glance.
 *
 * Times are epoch **seconds**, not milliseconds and not ISO strings. The
 * certificate is written by `Utf8JsonWriter` in C# and read by `JSON.parse` in
 * TypeScript, and an integer cannot disagree about a timezone suffix or a
 * fractional second the way two date serialisers can — which matters more here
 * than anywhere, because the two halves only find out they disagree when a
 * customer's licence is rejected in the field.
 *
 * Field order is load-bearing on the issuing side only: `encodeSigningInput`
 * serialises this object, and `LicenceSigner.Payload` writes the same keys in
 * the same order so a certificate is byte-identical whichever issued it.
 * Verification never re-serialises — it checks the signature over the text as
 * received — so an unknown field from a later BroadPaper is carried through
 * rather than rejected.
 */
export interface LicensePayload {
  /**
   * Payload schema version. Bumped only when the meaning of a field changes,
   * never to add one: `readPayload` keeps fields it does not know about, so a
   * later BroadPaper can extend the certificate without older SDKs refusing it.
   */
  schemaVersion: 1;
  /** Which signing key signed this. Lets keys be rotated without reissuing everything. */
  keyId: string;
  /**
   * BroadPaper's own identifier for *this certificate*, `lic_…`.
   *
   * One per issuance, so a renewal or an upgrade carries a new one and the
   * document it produced can be dated from it. The buyer is identified by
   * `organisationId`, which does not change.
   */
  licenseId: string;
  /** The licensed organisation's durable id. A Clerk organisation id. */
  organisationId: string;
  /** The organisation's name, for display in warnings and PDF metadata. */
  organisationName: string;
  product: typeof PRODUCT;
  plan: Plan;
  licenseType: LicenseType;
  /** Epoch seconds. */
  issuedAt: number;
  /**
   * When the right to *use* the software ends, in epoch seconds.
   *
   * Null means never, which only a perpetual licence says. A subscription
   * always carries a date.
   */
  validUntil: number | null;
  /**
   * When the right to *updates* ends, in epoch seconds.
   *
   * For a subscription this equals `validUntil` and nothing reads it: the term
   * ends, and both rights end with it. It is separate from `validUntil` because
   * for a perpetual licence the two genuinely differ, and that is the whole
   * reason the field is here before anything consults it.
   */
  updatesUntil: number;
}

/**
 * Why a licence is not being honoured, or how it is being honoured.
 *
 * These are deliberately coarse. A developer needs to know whether their PDFs
 * will carry the evaluation mark and, if so, what to do about it; a taxonomy of
 * eleven parse failures helps nobody.
 */
export type LicenseState =
  /** No licence was configured. The SDK works; PDFs carry the evaluation mark. */
  | "unlicensed"
  /** A licence was configured but could not be read, or its signature is wrong. */
  | "invalid"
  /** Signed by a key this build of the SDK does not trust. */
  | "untrusted"
  /** For a different product, or a certificate this SDK is too old to honour. */
  | "unsupported"
  /** Valid, signed, and inside its term. */
  | "active"
  /** Past `validUntil` but inside the grace period: honoured, and warned about. */
  | "grace"
  /** Past `validUntil` and past the grace period. */
  | "expired";

/** The outcome of checking a licence. Everything the SDK needs to decide. */
export interface LicenseStatus {
  state: LicenseState;
  /** True when PDFs render clean. `active` and `grace` only. */
  licensed: boolean;
  /** The payload, when one could be read and verified. */
  payload: LicensePayload | null;
  /** Which licence was bought, when it is being honoured. */
  plan: Plan | null;
  /** When the right to use ends, when known. Null for a licence with no end. */
  validUntil: Date | null;
  /** Days until `validUntil`; negative once expired. Null when there is no end date. */
  daysRemaining: number | null;
  /**
   * A developer-facing sentence. Always present for a state that is not
   * `active`, so callers can log it without composing their own prose.
   */
  message: string | null;
  /**
   * An informational line for a licence that *is* being honoured but is worth
   * knowing about — today, only an evaluation licence, which says when it ends.
   * Logged at info level, once. Optional so a status built by hand elsewhere
   * still type-checks.
   */
  notice?: string | null;
}

/** A public key this build of the SDK will accept signatures from. */
export interface TrustedKey {
  /** Matches `keyId` in the payload. */
  keyId: string;
  /** The raw 32-byte Ed25519 public key, base64url or base64. */
  publicKey: string;
  /** Free text for the key's provenance. Never displayed to end users. */
  note?: string;
}
